// collection-filters.js
// Gestion des filtres de collection avec AJAX et debouncing

class CollectionFilters {
  constructor() {
    this.forms = document.querySelectorAll("[data-filters-form]");
    this.drawer = document.querySelector("[data-filters-drawer]");
    this.drawerContent = document.querySelector("[data-filters-drawer-content]");
    this.toggleButtons = document.querySelectorAll("[data-filters-toggle]");
    this.closeButtons = document.querySelectorAll("[data-filters-drawer-close]");
    this.productGrid = document.querySelector("[data-product-grid]");
    this.debounceTimer = null;
    this.debounceDelay = 500; // 500ms de debounce

    if (this.forms.length === 0) return;

    this.init();
  }

  init() {
    // Ajouter la transition CSS sur la grille
    if (this.productGrid) {
      this.productGrid.style.transition = "opacity 200ms ease-in-out";
    }

    // Event listeners pour les formulaires (bouton "Filtrer" comme fallback)
    this.forms.forEach((form) => {
      form.addEventListener("submit", this.handleSubmit.bind(this));
    });

    // Event delegation pour les checkboxes (survit au remplacement AJAX)
    this.initCheckboxListeners();

    // Fermer les dropdowns <details> au clic en dehors (filtres + variant selectors)
    this.initDetailsCloseOnClickOutside();

    // Sort radios (fusionné depuis collection-sort.js)
    this.initSortRadios();

    // Clear buttons (event delegation pour gérer les boutons dynamiques)
    document.addEventListener("click", (e) => {
      const clearButton = e.target.closest("[data-filters-clear]");
      if (clearButton) {
        e.preventDefault();
        this.clearAllFilters();
      }
    });

    // Mobile drawer (event delegation pour survivre au remplacement AJAX)
    document.addEventListener("click", (e) => {
      // Ouvrir le drawer
      const toggleBtn = e.target.closest("[data-filters-toggle]");
      if (toggleBtn) {
        e.preventDefault();
        this.openDrawer();
        return;
      }

      // Fermer le drawer (bouton close ou overlay)
      const closeBtn = e.target.closest("[data-filters-drawer-close]");
      if (closeBtn) {
        this.closeDrawer();
      }
    });
  }

  initDetailsCloseOnClickOutside() {
    // Sélectionner tous les dropdowns (filtres + variant selectors)
    // Fusionné depuis collection-filters-dropdown.js
    const allDetails = document.querySelectorAll(
      ".filter-group details, .sort-group details, .variant-selector-group details"
    );

    document.addEventListener("click", (e) => {
      allDetails.forEach((details) => {
        // Si le clic est en dehors du details et qu'il est ouvert
        if (details.open && !details.contains(e.target)) {
          details.open = false;
        }
      });
    });

    // Fermer les autres details quand on en ouvre un
    allDetails.forEach((details) => {
      details.addEventListener("toggle", () => {
        if (details.open) {
          allDetails.forEach((otherDetails) => {
            if (otherDetails !== details) {
              otherDetails.removeAttribute("open");
            }
          });
        }
      });
    });
  }

  initSortRadios() {
    // Event delegation pour les radios de tri (fusionné depuis collection-sort.js)
    document.addEventListener("change", (e) => {
      const sortRadio = e.target.closest("[data-sort-radio]");
      if (!sortRadio) return;

      const form = sortRadio.closest("form[data-filters-form]");
      if (!form) return;

      // Le navigateur gère automatiquement l'état checked des radios du même groupe

      // Fermer le details avant d'appliquer le tri
      const details = sortRadio.closest("details");
      if (details) {
        details.open = false;
      }

      // Appliquer le tri via AJAX
      this.handleFilterChange(form);
    });
  }

  initCheckboxListeners() {
    // Event delegation pour les checkboxes (survit au remplacement AJAX)
    document.addEventListener("change", (e) => {
      const checkbox = e.target.closest(
        '[data-filters-form] input[type="checkbox"]:not([name^="size_range_"]):not([name^="price_range_"])'
      );
      if (!checkbox) return;

      const form = checkbox.closest("[data-filters-form]");
      if (form) {
        this.handleFilterChange(form);
      }
    });

    // Events custom des scripts de tranches (prix/taille)
    document.addEventListener("sizeRangeChange", (e) => {
      const form = e.target.closest("[data-filters-form]");
      if (form) this.handleFilterChange(form);
    });

    document.addEventListener("priceRangeChange", (e) => {
      const form = e.target.closest("[data-filters-form]");
      if (form) this.handleFilterChange(form);
    });
  }

  handleFilterChange(form) {
    // Annuler le timer précédent
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    // Mobile (drawer ouvert) = instantané, Desktop = debounce 500ms
    const drawer = document.querySelector("[data-filters-drawer]");
    const isMobile = drawer && drawer.dataset.state === "open";
    const delay = isMobile ? 0 : this.debounceDelay;

    this.debounceTimer = setTimeout(() => {
      this.applyFiltersAjax(form);
    }, delay);
  }

  async applyFiltersAjax(form) {
    try {
      // Fermer le drawer mobile immédiatement (parallèle au fetch)
      const drawer = document.querySelector("[data-filters-drawer]");
      if (drawer && drawer.dataset.state === "open") {
        this.closeDrawer();
      }

      // Loading state
      this.setLoadingState(true);

      const formData = new FormData(form);
      const params = new URLSearchParams();

      // Construire les params en ignorant les helpers (price_range_*, size_range_*)
      for (const [key, value] of formData.entries()) {
        if (!key.startsWith("price_range_") && !key.startsWith("size_range_") && value) {
          params.append(key, value);
        }
      }

      // Construire l'URL avec section_id pour récupérer juste la section
      const url = `${window.location.pathname}?${params.toString()}&section_id=collection-products`;

      // Fetch AJAX
      const response = await fetch(url);
      if (!response.ok) throw new Error("Network response was not ok");

      const html = await response.text();

      // Parser le HTML pour extraire la grille de produits
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const newProductGrid = doc.querySelector("[data-product-grid]");

      if (newProductGrid && this.productGrid) {
        // Remplacer la grille avec fade
        this.productGrid.style.opacity = "0";

        setTimeout(() => {
          this.productGrid.innerHTML = newProductGrid.innerHTML;
          this.productGrid.style.opacity = "1";

          // Ré-initialiser le comportement des details
          this.initDetailsCloseOnClickOutside();

          // Notifier les autres scripts (size-range-filter.js) de ré-initialiser
          document.dispatchEvent(new CustomEvent("filtersReloaded"));

          // Scroll smooth vers le haut de la grille
          this.productGrid.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 200);
      }

      // Mettre à jour l'URL du navigateur (pour partage/bookmark)
      const newUrl = `${window.location.pathname}?${params.toString()}`;
      window.history.pushState({ path: newUrl }, "", newUrl);
    } catch (_error) {
      // Fallback : recharger la page en cas d'erreur
      const formData = new FormData(form);
      const params = new URLSearchParams();
      for (const [key, value] of formData.entries()) {
        if (!key.startsWith("price_range_") && !key.startsWith("size_range_") && value) {
          params.append(key, value);
        }
      }
      window.location.href = `${window.location.pathname}?${params.toString()}`;
    } finally {
      this.setLoadingState(false);
    }
  }

  handleSubmit(e) {
    e.preventDefault();
    const form = e.target;

    // Annuler le debounce et appliquer immédiatement
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.applyFiltersAjax(form);
  }

  setLoadingState(isLoading) {
    if (this.productGrid) {
      if (isLoading) {
        this.productGrid.style.opacity = "0.5";
        this.productGrid.style.pointerEvents = "none";
      } else {
        this.productGrid.style.opacity = "1";
        this.productGrid.style.pointerEvents = "";
      }
    }
  }

  async clearAllFilters() {
    try {
      // Loading state
      this.setLoadingState(true);

      // Construire l'URL de base (sans params) avec section_id
      const url = `${window.location.pathname}?section_id=collection-products`;

      // Fetch AJAX
      const response = await fetch(url);
      if (!response.ok) throw new Error("Network response was not ok");

      const html = await response.text();

      // Parser le HTML pour extraire la grille de produits
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");
      const newProductGrid = doc.querySelector("[data-product-grid]");

      if (newProductGrid && this.productGrid) {
        // Remplacer la grille avec fade
        this.productGrid.style.opacity = "0";

        setTimeout(() => {
          this.productGrid.innerHTML = newProductGrid.innerHTML;
          this.productGrid.style.opacity = "1";

          // Ré-initialiser le comportement des details
          this.initDetailsCloseOnClickOutside();

          // Notifier les autres scripts (size-range-filter.js, price-range-filter.js) de ré-initialiser
          document.dispatchEvent(new CustomEvent("filtersReloaded"));

          // Scroll smooth vers le haut de la grille
          this.productGrid.scrollIntoView({ behavior: "smooth", block: "start" });

          // Fermer le drawer mobile si ouvert
          const drawer = document.querySelector("[data-filters-drawer]");
          if (drawer && drawer.dataset.state === "open") {
            this.closeDrawer();
          }
        }, 200);
      }

      // Mettre à jour l'URL du navigateur (retour à l'URL de base)
      window.history.pushState({ path: window.location.pathname }, "", window.location.pathname);
    } catch (_error) {
      // Fallback : recharger la page vers l'URL de base
      window.location.href = window.location.pathname;
    } finally {
      this.setLoadingState(false);
    }
  }

  openDrawer() {
    // Re-récupérer les éléments (peuvent avoir été remplacés par AJAX)
    this.drawer = document.querySelector("[data-filters-drawer]");
    this.drawerContent = document.querySelector("[data-filters-drawer-content]");

    if (!this.drawer || !this.drawerContent) return;

    this.drawer.classList.remove("hidden");
    this.drawer.dataset.state = "open";

    // Forcer le reflow pour la transition
    this.drawer.offsetHeight;

    this.drawerContent.classList.remove("translate-x-full");
    this.drawerContent.classList.add("translate-x-0");

    // Empêcher le scroll du body
    document.body.style.overflow = "hidden";
  }

  closeDrawer() {
    // Re-récupérer les éléments (peuvent avoir été remplacés par AJAX)
    this.drawer = document.querySelector("[data-filters-drawer]");
    this.drawerContent = document.querySelector("[data-filters-drawer-content]");

    if (!this.drawer || !this.drawerContent) return;

    this.drawer.dataset.state = "closed";
    this.drawerContent.classList.remove("translate-x-0");
    this.drawerContent.classList.add("translate-x-full");

    // Réactiver le scroll du body
    document.body.style.overflow = "";

    // Cacher le drawer après la transition
    setTimeout(() => {
      if (this.drawer.dataset.state === "closed") {
        this.drawer.classList.add("hidden");
      }
    }, 300);
  }
}

// Initialiser au chargement de la page
document.addEventListener("DOMContentLoaded", () => {
  new CollectionFilters();
});
